import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { ScanIdsData } from '@/models/Scans'
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  List,
  Switch,
  Typography,
} from '@mui/material'

import { UUID } from 'crypto'
import { useState } from 'react'
import { useFundingNeeds } from '@/utils/hooks/fundingNeeds'
import MobileMatchNeedsListItem from '@/components/mobile/MobileMatchNeedsListItem'
import { useScanIdsByNeedId } from '@/utils/hooks/scans'
import Loading from '@/components/Loading'
import ErrorMessage from '@/components/ErrorMessage'
import { getEffectiveOrgUuid } from '@/utils/utils'

export default function MobileMatchPage() {
  const [showArchived, setShowArchived] = useState(false)
  // const [showResults, setShowResults] = useState<null | number>(null)
  const [runScanOnAll] = useState(false)

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()

  if (!userLoading && orgs && orgs.length === 0) {
    window.location.href = '/'
  }

  const {
    orgData,
    orgErrors,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  const organisation = orgData.find((org) => org.uuid === effectiveOrgUuid)

  const isAnnualSubscriber = organisation?.is_annual_subscriber === true

  const {
    data: fundingNeeds,
    isLoading: fundingNeedsLoading,
    error,
  } = useFundingNeeds(token, organisation?.uuid as UUID)

  const {
    data: scanIds,
    isLoading: scanIdsLoading,
    isError: scanIdsError,
  } = useScanIdsByNeedId(
    token as string,
    (fundingNeeds && fundingNeeds?.map((fundingNeed) => fundingNeed.id)) || []
  ) as { data: ScanIdsData[]; isLoading: boolean; isError: boolean }

  if (userLoading || orgsLoading || fundingNeedsLoading || scanIdsLoading) {
    return <Loading />
  }

  if (isError || error || orgErrors.length > 0 || scanIdsError) {
    return <ErrorMessage />
  }

  const filteredNeeds = fundingNeeds?.filter(
    (need) => showArchived || !need.is_archived
  )
  const allFundingNeedsArchived = Boolean(
    fundingNeeds?.length && !fundingNeeds.some((need) => !need.is_archived)
  )

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          padding: '1rem',
          mb: 15,
        }}
      >
        <>
          {!fundingNeedsLoading &&
          (!fundingNeeds || fundingNeeds.length === 0) ? (
            <Box
              sx={{
                margin: '0 auto',
                marginTop: '1rem',
                width: '100%',
              }}
            >
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  mb: 3,
                  textAlign: 'center',
                  color: '#1C3045',
                }}
              >
                Find Funding Opportunity Matches
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  width: '90%',
                  px: 3,
                  py: 4,
                  mt: 2,
                  border: 1,
                  borderColor: '#1C3045',
                  borderRadius: 2,
                  mx: 'auto',
                  bgcolor: '#f7f7f7',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: '18px',
                    textAlign: 'center',
                    fontWeight: 500,
                  }}
                >
                  You don't have any Funding Requests yet!
                </Typography>
                <Typography
                  sx={{
                    mb: 3,
                    textAlign: 'center',
                    fontSize: '16px',
                  }}
                >
                  Add a Funding Request on the Funding Requests Page to get
                  started
                </Typography>
                <Button
                  variant="contained"
                  disableRipple
                  color="primary"
                  href="/funding-requests"
                  sx={{
                    width: '100%',
                    py: 1.5,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '16px',
                  }}
                >
                  Go to Funding Requests
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                margin: '0 auto',
                marginTop: '1rem',
                width: '100%',
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  mb: 3,
                  textAlign: 'left',
                  color: '#1C3045',
                }}
              >
                Find Funding Opportunity Matches
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '85vw',
                  margin: '0 auto',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showArchived}
                      onChange={() => setShowArchived(!showArchived)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1C3045',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                          {
                            backgroundColor: '#1C3045',
                          },
                        '& .MuiSwitch-thumb': {
                          border: showArchived
                            ? '1px solid #1C3045'
                            : '1px solid #666666',
                        },
                      }}
                    />
                  }
                  label="Show Archived"
                />

                <Typography
                  variant="caption"
                  sx={{
                    ml: 0,
                    fontSize: '14px',
                    textAlign: 'center',
                    width: '90%',
                  }}
                >
                  Archived funding requests aren't included when searching for
                  matches.
                </Typography>
              </Box>
              {allFundingNeedsArchived && (
                <Alert
                  severity="info"
                  sx={{ width: '85vw', margin: '0 auto', mt: 2 }}
                >
                  All your funding requests are archived, so nothing is being
                  matched. Add one on the Funding Requests page, or turn on
                  &quot;Show Archived&quot; to unarchive an existing request.
                </Alert>
              )}

              {/* <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '90vw',
                margin: '0 auto',
                mt: 5,
                mb: 3,
              }}
            >
              <Button
                variant="contained"
                disableRipple
                disabled={runScanOnAll}
                sx={{
                  bgcolor: '#1C3045',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: 2,
                  py: 1,
                  px: 2,
                  fontSize: '16px',
                  width: '100%',
                }}
                onClick={() => handleRunScanOnAll()}
              >
                Find Matches for all Funding Requests
              </Button> */}
              {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={inclRelationalAll}
                      onChange={() => setInclRelationalAll(!inclRelationalAll)}
                      sx={{
                        '&.Mui-checked': { color: '#1C3045' },
                      }}
                    />
                  }
                  label="Include Relational Opportunities"
                  labelPlacement="start"
                  sx={{
                    pt: 1,
                    width: '80vw',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-around',
                    margin: '0 auto',
                    fontSize: '14px',
                    mb: 2,
                  }}
                /> */}
              {/* </Box> */}

              <List
                sx={{
                  bgcolor: 'background.paper',
                  position: 'relative',
                  overflow: 'auto',
                  maxHeight: '60vh',
                  '& ul': { padding: 0 },
                  width: '100%',
                }}
              >
                <Divider sx={{ mt: 1 }} />
                {organisation &&
                  token &&
                  fundingNeeds &&
                  scanIds &&
                  filteredNeeds
                    ?.sort((a, b) => b.id - a.id)
                    .map((fundingNeed) => (
                      <Box key={fundingNeed.id} sx={{ width: '100%' }}>
                        <MobileMatchNeedsListItem
                          token={token}
                          scanIds={
                            scanIds.find(
                              (item) =>
                                item && item.fundingNeedId === fundingNeed.id
                            )?.scanIds
                          }
                          fundingNeed={fundingNeed || []}
                          runScanOnAll={runScanOnAll}
                          isAnnualSubscriber={isAnnualSubscriber}
                        />
                        <Divider />
                      </Box>
                    ))}
              </List>
            </Box>
          )}
        </>
      </Box>
    </>
  )
}
